import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { GraduationCap, ClipboardList, ListChecks, LogOut, Shield, TrendingUp, Repeat, Sparkles, School } from "lucide-react";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isStaff = user && user.role === "sub_admin";
  const isInfluencer = user && user.role === "influencer";
  const isPartner = user && user.role === "partner";
  const isStudent = user && !isStaff && isInfluencer === false && user.role !== "admin";
  const track = user?.track;
  const switchLink = { to: "/dashboard/track", label: "Switch side", icon: Repeat };
  const sideLinks = track === "nsfas"
    ? [{ to: "/dashboard/nsfas", label: "NSFAS Guide", icon: GraduationCap }]
    : track === "colleges"
    ? [{ to: "/dashboard/colleges", label: "Colleges", icon: School }, { to: "/dashboard/applications", label: "Applications", icon: ListChecks }]
    : track === "universities"
    ? [{ to: "/dashboard/marks", label: "My Marks", icon: ClipboardList }, { to: "/dashboard/courses", label: "Universities", icon: GraduationCap }, { to: "/dashboard/applications", label: "Applications", icon: ListChecks }]
    : [];
  const studentNav = [switchLink, ...sideLinks, { to: "/dashboard/help", label: "Ask AI", icon: Sparkles }];
  const sideBadge = isStudent && track && track !== "study" ? { universities: "University side", colleges: "College side", nsfas: "NSFAS" }[track] : null;
  const nav = isStaff
    ? [{ to: "/dashboard/admin", label: "Applications", icon: Shield }]
    : isInfluencer
    ? [{ to: "/dashboard/influencer", label: "Dashboard", icon: TrendingUp }]
    : isPartner
    ? [{ to: "/dashboard/partner", label: "Dashboard", icon: TrendingUp }]
    : studentNav;
  if (user && user.role === "admin") {
    nav.push({ to: "/dashboard/admin", label: "Admin", icon: Shield });
  }

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to={user ? (user.role === "sub_admin" ? "/dashboard/admin" : user.role === "influencer" ? "/dashboard/influencer" : user.role === "partner" ? "/dashboard/partner" : "/dashboard/marks") : "/"}>
          <Logo />
        </Link>
        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {sideBadge && <span data-testid="current-side-badge" className="mr-2 rounded-full bg-[#F5C32C]/25 text-[#0B2447] text-xs font-semibold px-3 py-1">{sideBadge}</span>}
            {nav.map((n) => {
              const active = location.pathname === n.to;
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    active ? "bg-[#0B2447] text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {n.label}
                </Link>
              );
            })}
          </nav>
        )}
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <NotificationBell />
              <span className="hidden sm:block text-sm text-slate-500">{user.email}</span>
              <Button
                variant="ghost"
                size="sm"
                data-testid="logout-btn"
                onClick={() => {
                  localStorage.removeItem("yva_side_chosen");
                  logout();
                  navigate("/");
                }}
              >
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <>
              <nav className="hidden lg:flex items-center gap-1 mr-2" data-testid="public-nav">
                {[["/how-it-works", "How It Works"], ["/resources", "Guides"], ["/faq", "FAQ"], ["/about", "About"], ["/contact", "Contact"]].map(([to, label]) => (
                  <Link key={to} to={to} data-testid={`pubnav-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith(to) ? "text-[#0B2447] bg-slate-100" : "text-slate-600 hover:bg-slate-100"}`}>{label}</Link>
                ))}
              </nav>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login-btn">
                Login
              </Button>
              <Button
                size="sm"
                className="bg-[#0B2447] hover:bg-[#153461]"
                onClick={() => navigate("/register")}
                data-testid="header-register-btn"
              >
                Get Started
              </Button>
            </>
          )}
        </div>
      </div>
      {user && (
        <nav className="md:hidden flex items-center gap-1 overflow-x-auto px-4 pb-2" data-testid="mobile-nav">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${location.pathname === n.to ? "bg-[#0B2447] text-white" : "bg-slate-100 text-slate-600"}`}>
                <Icon className="w-3.5 h-3.5" />{n.label}
              </Link>
            );
          })}
        </nav>
      )}
    </header>
  );
}
